<template>
  <div class="process-surveillance one-section-per-page">
    <filters-section :showLocation="true" :includeUnits="true" :filterDisplayKeys="getFilterKeys()"
      :moreFilters="global.additionalFilters" :dateRangeStart="global.defaultDateStart"
      :defaultValues="global.filterDefaults" v-on:change="formatFilters">
      <div slot="title">
        <el-button data-testid="training-guide" size="small" @click="downloadTrainingGuide()">Training Guide</el-button>
      </div>
    </filters-section>

    <page-section title="Lab Results" v-loading="summarySection.loading">
      <div style="display:flex; margin-top: 1rem;">
        <div style="display:flex; gap: 1rem; flex-wrap: wrap; margin: 0 auto;">
          <el-card>
            <div class="card-content">
              <strong>{{ summarySection.totalCases }}</strong> Total UTI infections reviewed
            </div>
          </el-card>
          <el-card>
            <div class="card-content">
              <template v-if="summarySection.lab.totalOrdered > 0">
                <strong>{{ summarySection.lab.complianceRate }}%</strong>
              </template>
              <template v-else>
                <strong>--</strong>
              </template>
              Protocol compliance rate
              <span>(criteria met / labs ordered)</span>
            </div>
          </el-card>
          <el-card>
            <div class="card-content">
              <strong>{{ summarySection.lab.flaggedCount }}</strong> Flagged records requiring review
            </div>
          </el-card>
        </div>
      </div>
      <div style="margin: 1rem;">
        <el-card>
          <h3>Infection flow</h3>
          <flow-diagram title="UTI Lab Results — Infection flow" :source-value="summarySection.totalCases"
            :source-label="`UTI infections`" :met-value="summarySection.criteriaMetCount" :met-label="`Criteria met`"
            :not-met-value="summarySection.criteriaNotMetCount" :not-met-label="`Criteria not met`"
            :action-met-value="summarySection.lab.criteriaMetWith" :action-met-label="`— Lab ordered, criteria met ✓`"
            :no-action-met-value="summarySection.lab.criteriaMetWithout"
            :no-action-met-label="`— No lab ordered, criteria met ⚑`"
            :action-not-met-value="summarySection.lab.criteriaNotMetWith"
            :action-not-met-label="`— Lab ordered, no criteria ⚑`"
            :no-action-not-met-value="summarySection.lab.criteriaNotMetWithout"
            :no-action-not-met-label="`— No lab ordered, no criteria`"
            :edge-source-met="summarySection.criteriaMetCount" :edge-source-not-met="summarySection.criteriaNotMetCount"
            :edge-met-action="summarySection.lab.criteriaMetWith"
            :edge-met-no-action="summarySection.lab.criteriaMetWithout"
            :edge-not-met-action="summarySection.lab.criteriaNotMetWith"
            :edge-not-met-no-action="summarySection.lab.criteriaNotMetWithout" :legend-failure="`⚑ compliance alert`"
            :formula="`Compliance = labs ordered with criteria met ÷ total labs ordered = ${summarySection.lab.criteriaMetWith} ÷ ${summarySection.lab.totalOrdered} = ${summarySection.lab.complianceRate}%`"></flow-diagram>
        </el-card>
        <div style="display:flex; width:100%;gap: 1rem;">
          <el-card style="flex:1 1 0; min-width: 0;">
            <h3 style="text-transform: uppercase;">
              Compliance Alert &mdash; lab without criteria
              <el-tag type="danger" size="mini">{{ summarySection.lab.orderedNoCriteria.length }} Records</el-tag>
            </h3>
            <p class="card-description">
              Lab was ordered but the patient did not meet clinical criteria for a UTI.
            </p>

            <el-table :data="pagedLabOrderedNoCriteria" :show-header="true" class="clickable-rows" style="width: 100%"
              @row-click="selectInfectionCase">
              <el-table-column label="Name">
                <template slot-scope="scope">
                  {{ scope.row.name }}
                </template>
              </el-table-column>
              <el-table-column label="Report Criteria">
                <template slot-scope="scope">
                  <el-tag type="gray">Criteria Not Met</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Lab">
                <template slot-scope="scope">
                  <el-tag size="mini" type="primary">Ordered</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Action">
                <template slot-scope="scope">
                  Clinical review
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              @size-change="size => { tablePagination.labOrderedNoCriteria.size = size; tablePagination.labOrderedNoCriteria.page = 1; }"
              @current-change="page => { tablePagination.labOrderedNoCriteria.page = page; }"
              :current-page="tablePagination.labOrderedNoCriteria.page" :page-sizes="[5, 10, 20, 100]"
              :page-size="tablePagination.labOrderedNoCriteria.size" layout="sizes, prev, pager, next"
              :total="summarySection.lab.orderedNoCriteria.length" style="margin-top: 1rem; text-align: center;">
            </el-pagination>
          </el-card>
          <el-card style="flex:1 1 0; min-width: 0;">
            <h3 style="text-transform: uppercase;">
              Watch list &mdash; criteria met, no lab
              <el-tag type="warning" size="mini">{{ summarySection.lab.criteriaMetNoOrder.length }} Record{{
                summarySection.lab.criteriaMetNoOrder.length === 1 ? '' : 's'
                }}</el-tag>
            </h3>
            <p class="card-description">
              Clinical criteria of UTI was met, but no lab result was ordered or documented.
            </p>

            <el-table :data="pagedLabCriteriaMetNoOrder" :show-header="true" class="clickable-rows" style="width: 100%"
              @row-click="selectInfectionCase">
              <el-table-column label="Name">
                <template slot-scope="scope">
                  {{ scope.row.name }}
                </template>
              </el-table-column>
              <el-table-column label="Report Criteria">
                <template slot-scope="scope">
                  <el-tag type="danger">Criteria Met</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Lab">
                <template slot-scope="scope">
                  <el-tag size="mini" type="warning">Missing</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Action">
                <template slot-scope="scope">
                  Check documentation
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              @size-change="size => { tablePagination.labCriteriaMetNoOrder.size = size; tablePagination.labCriteriaMetNoOrder.page = 1; }"
              @current-change="page => { tablePagination.labCriteriaMetNoOrder.page = page; }"
              :current-page="tablePagination.labCriteriaMetNoOrder.page" :page-sizes="[5, 10, 20, 100]"
              :page-size="tablePagination.labCriteriaMetNoOrder.size" layout="sizes, prev, pager, next"
              :total="summarySection.lab.criteriaMetNoOrder.length" style="margin-top: 1rem; text-align: center;">
            </el-pagination>
          </el-card>
        </div>
      </div>
    </page-section>

    <page-section title="Antibiotic Usage" v-loading="summarySection.loading">
      <div style="display:flex; margin-top: 1rem;">
        <div style="display:flex; gap: 1rem; flex-wrap: wrap; margin: 0 auto;">
          <el-card>
            <div class="card-content">
              <strong>{{ summarySection.totalCases }}</strong> Total UTI infections reviewed
            </div>
          </el-card>
          <el-card>
            <div class="card-content">
              <template v-if="summarySection.medication.totalOrdered > 0">
                <strong>{{ summarySection.medication.complianceRate }}%</strong>
              </template>
              <template v-else>
                <strong>--</strong>
              </template>
              Protocol compliance rate
              <span>(criteria met / antibiotic prescribed)</span>
            </div>
          </el-card>
          <el-card>
            <div class="card-content">
              <strong>{{ summarySection.medication.flaggedCount }}</strong> Flagged records requiring review
            </div>
          </el-card>
        </div>
      </div>
      <div style="margin: 1rem;">
        <el-card>
          <h3>Infection flow</h3>

          <flow-diagram title="UTI Antibiotic Usage — Infection flow" :source-value="summarySection.totalCases"
            :source-label="`UTI infections`" :met-value="summarySection.criteriaMetCount" :met-label="`Criteria met`"
            :not-met-value="summarySection.criteriaNotMetCount" :not-met-label="`Criteria not met`"
            :action-met-value="summarySection.medication.criteriaMetWith"
            :action-met-label="`— Abx prescribed, criteria met ✓`"
            :no-action-met-value="summarySection.medication.criteriaMetWithout"
            :no-action-met-label="`— No abx prescribed, criteria met ⚑`"
            :action-not-met-value="summarySection.medication.criteriaNotMetWith"
            :action-not-met-label="`— Abx prescribed, no criteria ⚑`"
            :no-action-not-met-value="summarySection.medication.criteriaNotMetWithout"
            :no-action-not-met-label="`— No abx prescribed, no criteria`"
            :edge-source-met="summarySection.criteriaMetCount" :edge-source-not-met="summarySection.criteriaNotMetCount"
            :edge-met-action="summarySection.medication.criteriaMetWith"
            :edge-met-no-action="summarySection.medication.criteriaMetWithout"
            :edge-not-met-action="summarySection.medication.criteriaNotMetWith"
            :edge-not-met-no-action="summarySection.medication.criteriaNotMetWithout"
            :legend-failure="`⚑ compliance alert`"
            :formula="`Compliance = Abx prescribed with criteria met ÷ total Abx prescribed = ${summarySection.medication.criteriaMetWith} ÷ ${summarySection.medication.totalOrdered} = ${summarySection.medication.complianceRate}%`"></flow-diagram>

        </el-card>
        <div style="display:flex; width:100%;gap: 1rem;">
          <el-card style="flex:1 1 0; min-width: 0;">
            <h3 style="text-transform: uppercase;">
              Compliance Alert &mdash; abx without criteria
              <el-tag type="danger" size="mini">{{ summarySection.medication.orderedNoCriteria.length }}
                Records</el-tag>
            </h3>
            <p class="card-description">
              Antibiotics prescribed but patient did not meet clinical criteria for a UTI. Risk of inappropriate Abx
              use.
            </p>
            <!-- medication.orderedNoCriteria -->
            <el-table :data="pagedMedicationOrderedNoCriteria" :show-header="true" class="clickable-rows"
              style="width: 100%" @row-click="selectInfectionCase">
              <el-table-column label="Name">
                <template slot-scope="scope">
                  {{ scope.row.name }}
                </template>
              </el-table-column>
              <el-table-column label="Report Criteria">
                <template slot-scope="scope">
                  <el-tag type="gray">Criteria Not Met</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Abx prescribed">
                <template slot-scope="scope">
                  <el-tag type="primary" size="mini">Yes</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="action" label="Action">
                <template slot-scope="scope">
                  Clinical Review
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              @size-change="size => { tablePagination.medicationOrderedNoCriteria.size = size; tablePagination.medicationOrderedNoCriteria.page = 1; }"
              @current-change="page => { tablePagination.medicationOrderedNoCriteria.page = page; }"
              :current-page="tablePagination.medicationOrderedNoCriteria.page" :page-sizes="[5, 10, 20, 100]"
              :page-size="tablePagination.medicationOrderedNoCriteria.size" layout="sizes, prev, pager, next"
              :total="summarySection.medication.orderedNoCriteria.length" style="margin-top: 1rem; text-align: center;">
            </el-pagination>
          </el-card>
          <el-card style="flex:1 1 0; min-width: 0;">
            <h3 style="text-transform: uppercase;">
              Watch list &mdash; criteria met, no abx documented
              <el-tag type="warning" size="mini">{{ summarySection.medication.criteriaMetNoOrder.length }}
                Record{{ summarySection.medication.criteriaMetNoOrder.length === 1 ? '' : 's'
                }}</el-tag>
            </h3>
            <p class="card-description">
              Patient met criteria but no antibiotic is documented. May indicate undocumented treatment or a missed
              prescription.
            </p>
            <!-- medication.criteriaMetNoOrder -->
            <el-table :data="pagedMedicationCriteriaMetNoOrder" :show-header="true" class="clickable-rows"
              style="width: 100%" @row-click="selectInfectionCase">
              <el-table-column label="Name">
                <template slot-scope="scope">
                  {{ scope.row.name }}
                </template>
              </el-table-column>
              <el-table-column label="Report Criteria">
                <template slot-scope="scope">
                  <el-tag type="danger">Criteria Met</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Abx prescribed">
                <template slot-scope="scope">
                  <el-tag type="warning" size="mini">Undocumented</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="action" label="Action">
                <template slot-scope="scope">
                  Check documentation
                </template>
              </el-table-column>
            </el-table>
            <el-pagination
              @size-change="size => { tablePagination.medicationCriteriaMetNoOrder.size = size; tablePagination.medicationCriteriaMetNoOrder.page = 1; }"
              @current-change="page => { tablePagination.medicationCriteriaMetNoOrder.page = page; }"
              :current-page="tablePagination.medicationCriteriaMetNoOrder.page" :page-sizes="[5, 10, 20, 100]"
              :page-size="tablePagination.medicationCriteriaMetNoOrder.size" layout="sizes, prev, pager, next"
              :total="summarySection.medication.criteriaMetNoOrder.length"
              style="margin-top: 1rem; text-align: center;">
            </el-pagination>
          </el-card>
        </div>
      </div>
    </page-section>

    <page-section :title="`Analysis Details (${detailsSection.pagination.total})`" v-loading="detailsSection.loading"
      style="margin-bottom: 3rem;">
      <template slot="title">
        <el-button data-testid="reset-case-filter" size="small" :disabled="!infectionCaseId"
          @click="infectionCaseId = null">Reset</el-button>
        <el-button data-testid="export-observations" size="small" @click="exportObservationsToExcel()">Export
          Data</el-button>
        <el-pagination @size-change="
          size => {
            detailsSection.pagination.size = size;
          }
        " @current-change="
            page => {
              detailsSection.pagination.page = page;
            }
          " :page-sizes="[5, 10, 20, 100]" :page-size="detailsSection.pagination.size"
          layout="sizes, prev, pager, next" :total="detailsSection.pagination.total">
        </el-pagination>
      </template>
      <el-table :data="detailsSection.data" v-if="!detailsSection.loadingReports" v-loading="detailsSection.loading"
        style="width: 100%" v-on:row-click="openInfectionCaseDetail">
        <el-table-column prop="name" label="Name (MRN)" fixed width="180">
          <template slot-scope="scope">
            <span v-if="scope.row.client">{{ clientName(scope.row) }}<br />({{ isStaff(scope.row.client) ?
              scope.row.client.staffId : $configStore.patientIdType() ? scope.row.client[$configStore.patientIdType()] :
                scope.row.client.patientId }})</span>
            <span v-else>{{ scope.row.name }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="sites && sites.length > 1" label="Facility" width="160">
          <template slot-scope="scope">
            {{
              isStaff(scope.row.client)
                ? scope.row.client.facilities
                  .map(facilityNameByFacilityId)
                  .filter(site => site)
                  .join(", ")
                : facilityNameByUnitId(scope.row.unitId)
            }}
          </template>
        </el-table-column>
        <el-table-column label="Onset Location" width="160">
          <template slot-scope="scope">
            {{ scope.row.location || "" }}
          </template>
        </el-table-column>
        <el-table-column label="Infection Site/Type" width="160">
          <template slot-scope="scope">
            <span class="color-code-label-0" v-if="scope.row.symptomType === 'GI'"></span>
            <span class="color-code-label-2" v-else-if="scope.row.symptomType === 'UTI'"></span>
            <span class="color-code-label-1" v-else-if="scope.row.symptomType === 'Respiratory'"></span>
            <span class="color-code-label-3" v-else></span>
            {{ scope.row.symptomType || scope.row.infectionSite }}
            {{ scope.row.infectionType ? " / " + scope.row.infectionType : "" }}
          </template>
        </el-table-column>
        <el-table-column label="Status" width="70">
          <template slot-scope="scope">
            <img class="symptom-status-image" :src="statusImage(scope.row)" />
          </template>
        </el-table-column>
        <el-table-column label="Symptoms" width="250">
          <template slot-scope="scope">
            <el-tag v-for="d in sortedSymptom(scope.row.symptoms)" :key="d.id + d.displayText" :type="tagType(d)">{{
              d.displayText }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="criteria" label="Surveillance Criteria" width="150"
          v-if="$configStore.rulesEngineEnabled()">
          <template slot-scope="scope">
            <el-tag v-if="evaluateCriteria(scope.row) === true" type="danger">Criteria Met</el-tag>
            <el-tag v-else-if="evaluateCriteria(scope.row) === false" type="gray">Criteria Not Met</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="UTI Report Criteria" width="150">
          <template slot-scope="scope">
            <el-tag type="danger" v-if="scope.row.utiReportCriteria">Criteria Met</el-tag>
            <el-tag type="gray" v-else>Criteria Not Met</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Outcome" width="100">
          <template scope="scope">
            <div>
              {{ scope.row.outcome || "Not Resolved" }}
              {{ scope.row.outcome && scope.row.outcomeSubType ? ` /
              ${scope.row.outcomeSubType}` : "" }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Primary Organism" width="150">
          <template slot-scope="scope">
            {{ scope.row.primaryOrganism || "" }}
          </template>
        </el-table-column>
        <el-table-column label="Treatment" width="200">
          <template slot-scope="scope">
            <div class="rx" v-for="rx in scope.row.prescriptions" :key="rx">{{ rx }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Onset Date" prop="onsetDate">
          <template slot-scope="scope" v-if="scope.row.onsetDate">
            {{ scope.row.onsetDate | moment($configStore.dateFormat()) }}
          </template>
        </el-table-column>
      </el-table>
    </page-section>


    <!-- Infection Case Detail View -->
    <el-dialog class="infection-case-detail-dialog" top="75px" v-on:close="
      cancelInfectionCaseDetail();
    getCases();
    " :title="detailsSection.editor.data ? clientName(detailsSection.editor.data) : ''"
      v-if="detailsSection.editor.data" v-model="detailsSection.editor.visible" size="large"
      :close-on-click-modal="false" :close-on-press-escape="false">
      <infection-case-detail :clientName="clientName(detailsSection.editor.data)"
        :selectedCase="detailsSection.editor.data" @dismiss="
          cancelInfectionCaseDetail();
        getCases();
        "></infection-case-detail>
    </el-dialog>

    <progress-overlay v-if="detailsSection.exportingObservations" :progress="detailsSection.observationExportProgress"
      v-on:progress-cancelled="cancelObservationExport" title="Download In Progress"></progress-overlay>

    <el-dialog title="Error, please try again" v-model="detailsSection.exportingErrorDialog"
      :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
      There was an error exporting Observations. Please try again.
      <div style="display: flex; justify-content: center; margin-top: 25px">
        <el-button data-testid="retry-export" @click="
          detailsSection.exportingErrorDialog = false;
        exportObservationsToExcel();
        ">Refresh</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { downloadXlsx } from "../../../spreadsheet";
import moment from "moment";
import util from "../../../util";
import transformer from "../../../transformer";
import api from "../../../services/restClient";
import ProgressOverlay from "../../../components/ProgressBarOverlay";
import InfectionCaseDetail from "../../InfectionCaseDetail";
import FiltersSection from "../../Shared/FiltersSection";
import PageSection from "../../Shared/PageSection";
import FlowDiagram from "./FlowDiagram";

export default {
  name: "process-surveillance-page",
  components: {
    "filters-section": FiltersSection,
    "page-section": PageSection,
    "flow-diagram": FlowDiagram,
    "progress-overlay": ProgressOverlay,
    "infection-case-detail": InfectionCaseDetail,
  },
  watch: {
    "detailsSection.pagination.size"() {
      this.getCases();
    },
    "detailsSection.pagination.page"() {
      this.getCases();
    },
    infectionCaseId() {
      this.getCases(this.infectionCaseId);
    }
  },
  data() {
    return {
      global: {
        filters: {},
        defaultDateStart: moment().subtract(90, "days").toDate(),
        facilities: [],
        additionalFilters: [],
        filterDefaults: { clientType: "Client", basicInfectionSites: ["UTI"] },
      },
      summarySection: {
        loading: true,
        totalCases: 0,
        criteriaMetCount: 0,
        criteriaNotMetCount: 0,
        lookupTables: {
          criteriaMet: {},
          labsCriteriaMetNoOrder: {},
          labsOrderedNoCriteria: {},
          medicationCriteriaMetNoOrder: {},
          medicationOrderedNoCriteria: {}
        },
        lab: {
          complianceRate: 0,
          criteriaMetNoOrder: [],
          criteriaMetWith: 0,
          criteriaMetWithout: 0,
          criteriaNotMetWith: 0,
          criteriaNotMetWithout: 0,
          flaggedCount: 0,
          totalOrdered: 0,
          orderedNoCriteria: []
        },
        medication: {
          complianceRate: 0,
          criteriaMetNoOrder: [],
          criteriaMetWith: 0,
          criteriaMetWithout: 0,
          criteriaNotMetWith: 0,
          criteriaNotMetWithout: 0,
          flaggedCount: 0,
          totalOrdered: 0,
          orderedNoCriteria: []
        }
      },
      detailsSection: {
        loading: true,
        loadingReports: false,
        editor: {
          data: null,
          visible: false
        },
        pagination: {
          page: 1,
          size: 10,
          total: 0
        },
        exportingObservations: false,
        observationExportProgress: 0,
        exportingErrorDialog: false,
        data: []
      },
      sites: [],
      units: [],
      medicationsLookup: {},
      labReports: {},
      statusImageMap: {
        GI: "bug2",
        UTI: "bug3",
        Respiratory: "bug1",
        Skin: "bug4"
      },
      infectionCaseId: null,
      tablePagination: {
        labOrderedNoCriteria: { page: 1, size: 5 },
        labCriteriaMetNoOrder: { page: 1, size: 5 },
        medicationOrderedNoCriteria: { page: 1, size: 5 },
        medicationCriteriaMetNoOrder: { page: 1, size: 5 }
      },
    };
  },
  computed: {
    pagedLabOrderedNoCriteria() {
      return this.paginate(this.summarySection.lab.orderedNoCriteria, this.tablePagination.labOrderedNoCriteria);
    },
    pagedLabCriteriaMetNoOrder() {
      return this.paginate(this.summarySection.lab.criteriaMetNoOrder, this.tablePagination.labCriteriaMetNoOrder);
    },
    pagedMedicationOrderedNoCriteria() {
      return this.paginate(this.summarySection.medication.orderedNoCriteria, this.tablePagination.medicationOrderedNoCriteria);
    },
    pagedMedicationCriteriaMetNoOrder() {
      return this.paginate(this.summarySection.medication.criteriaMetNoOrder, this.tablePagination.medicationCriteriaMetNoOrder);
    },
  },
  methods: {
    paginate(data, pagination) {
      const rows = data || [];
      const start = (pagination.page - 1) * pagination.size;
      return rows.slice(start, start + pagination.size);
    },
    getFilterKeys() {
      return ["infectionOnsetDate", "basicInfectionSiteSubTypes", "resolutions", "caseStatus", "origins"];
    },
    downloadTrainingGuide() {
      window.open("/static/UTI_Process_Surveillance_Training_Guide.pdf", "_blank");
    },
    formatFilters(rawFilters) {
      const filterValues = Object.fromEntries(
        Object.entries(rawFilters)
          .filter(([k, v]) => v !== 0)
          .map(([k, v]) => {
            switch (k) {
              case "facilities":
                k = "facIds";
                break;
              case "units":
                k = "unitIds";
                break;
              case "resolutions":
                k = "outcomes";
                v = v[0] === 0 ? ["all"] : [v[0]];
                break;
              case "basicInfectionSiteSubTypes":
                k = "infectionSiteSubTypes";
                break;
            }
            return [k, v];
          })
      );
      this.global.filters = { ...filterValues };
      this.global.filters.onsetDateFrom = moment(filterValues.infectionOnsetDate[0]).startOf("day").format();
      this.global.filters.onsetDateTo = moment(filterValues.infectionOnsetDate[1]).endOf("day").format();
      delete this.global.filters.infectionOnsetDate;
      delete this.global.filters.clientType;

      this.summarySection.loading = true;
      this.detailsSection.loading = true;
      this.getSummary().then(() => this.getCases());
    },
    async getSummary() {
      this.summarySection.loading = true;
      const response = await this.$http
        .post(`${window.CONFIG.infection_api}/uti-process-surveillance/summary`, {
          ...this.global.filters,
        })
        .then(resp => resp.body)
        .catch(err => {
          return null;
        });

      if (!response) {
        this.summarySection.loading = false;
        return;
      }

      this.summarySection = { ...this.summarySection, ...response };
      this.summarySection.lookupTables = {
        criteriaMet: Object.fromEntries((response.criteriaMetCaseIds ?? []).map(id => [id, true])),
        labsCriteriaMetNoOrder: util.arrayToObj(response.lab.criteriaMetNoOrder, "id"),
        labsOrderedNoCriteria: util.arrayToObj(response.lab.orderedNoCriteria, "id"),
        medicationCriteriaMetNoOrder: util.arrayToObj(response.medication.criteriaMetNoOrder, "id"),
        medicationOrderedNoCriteria: util.arrayToObj(response.medication.orderedNoCriteria, "id")
      };

      this.tablePagination.labOrderedNoCriteria.page = 1;
      this.tablePagination.labCriteriaMetNoOrder.page = 1;
      this.tablePagination.medicationOrderedNoCriteria.page = 1;
      this.tablePagination.medicationCriteriaMetNoOrder.page = 1;

      this.summarySection.loading = false;
    },
    selectInfectionCase(row) {
      this.infectionCaseId = row.id;
    },
    openInfectionCaseDetail(row) {
      this.detailsSection.editor.data = transformer.mapInfectionCaseToLegacyModel(JSON.parse(JSON.stringify(row)));
      this.detailsSection.editor.visible = true;
    },
    cancelInfectionCaseDetail() {
      this.detailsSection.editor.data = null;
      this.detailsSection.editor.visible = false;
    },
    async getCases(infectionCaseId) {
      this.detailsSection.loading = true;
      const response = await this.$http
        .post(`${window.CONFIG.infection_api}/search`, infectionCaseId ? { infectionCaseId } : {
          ...this.global.filters,
          infectionSites: ["UTI"],
          clientType: "Client",
          page: this.detailsSection.pagination.page,
          size: this.detailsSection.pagination.size
        })
        .then(resp => resp.body)
        .catch(err => {
          return null;
        });

      if (!response) {
        this.detailsSection.loading = false;
        return;
      }

      this.detailsSection.pagination.total = response.totalElements;
      const cases = response.content ?? [];
      if (cases.length) {
        let caseTotal = cases.length;
        let processed = 0;
        this.labReports = {};
        this.detailsSection.loadingReports = true;
        let remainingReports = caseTotal;
        cases.forEach(async c => {
          if (c.origin == null) {
            c.origin = "Not Selected";
          }
          // Set synchronously (before any await) so the property exists when
          // detailsSection.data is assigned and Vue makes it reactive
          c.utiReportCriteria = !!this.summarySection.lookupTables.criteriaMet[c.id];
          c.symptoms.sort(function (a, b) {
            return a.colorCode > b.colorCode;
          });
          c.otherSymptoms = [];
          api.getCustomSymptomForDetail(c.id, r => {
            c.otherSymptoms = r.body.otherSymptoms || [];
            processed = processed + 1;

            if (processed === caseTotal) {
              this.detailsSection.data = cases;
            }
          });

          c.client.precautionDetails = await this.$http
            .get(`${window.CONFIG.precaution_api}`, {
              params: {
                clientId: c.client.id,
                symptomDetailId: c.id
              }
            })
            .then(resp => resp.json())
            .then(json => json.content);

          c.prescriptions = [];

          const prescriptions = await this.$http
            .get(`${window.CONFIG.treatement_api}/prescriptions`, {
              params: {
                clientId: c.client.id,
                symptomDetailId: c.id
              }
            })
            .then(resp => resp.json());
          c.prescriptions = this.transformMedications(prescriptions.content);

          api.getLabReport(c.id, response => {
            if (response) {
              this.labReports[c.id] = response.body || [];
            }
            remainingReports--;
            if (remainingReports === 0) {
              this.detailsSection.loadingReports = false;
            }
          });
        });
      } else {
        this.detailsSection.data = [];
      }
      this.detailsSection.loading = false;
    },
    clientName(symptomDetail) {
      return this.getClientName(symptomDetail.client);
    },
    getClientName(clientData) {
      const firstName = clientData.firstName ? clientData.firstName + " " : "";
      const middleName = clientData.middleName ? clientData.middleName + " " : "";
      const lastName = clientData.lastName ? clientData.lastName : "";
      return firstName + middleName + lastName;
    },
    isStaff(client) {
      return client ? client.clientType === "Staff" : false;
    },
    statusImage(detail) {
      let result = "";
      let filename;
      if (detail.symptomType) {
        filename = this.statusImageMap[detail.symptomType];
        if (!detail.status) {
          filename = filename + "s";
        }
        result = "/static/" + filename + ".png";
      }

      if (result === "") {
        filename = "bug4";
        if (!detail.status) {
          filename = filename + "s";
        }
        result = "/static/" + filename + ".png";
      }

      return result;
    },
    tagType(type) {
      if (type.colorCode === 0) return "success";
      if (type.colorCode === 1) return "primary";
      if (type.colorCode === 2) return "warning";
      if (type.colorCode === 3) return "danger";
      return "gray";
    },
    sortedSymptom(symptoms) {
      if (!symptoms || symptoms.length <= 1) {
        return symptoms;
      }
      const sortedSymptoms = [];
      symptoms.forEach(s => sortedSymptoms.push(s));
      sortedSymptoms.sort(function (a, b) {
        let compA = a.classType || "";
        let compB = b.classType || "";

        if (compA === "GI") compA = "a" + a.displayText;
        else if (compA === "Respiratory") compA = "b" + a.displayText;
        else if (compA === "UTI") compA = "c" + a.displayText;
        else compA = "d" + a.displayText;

        if (compB === "GI") compB = "a" + b.displayText;
        else if (compB === "Respiratory") compB = "b" + b.displayText;
        else if (compB === "UTI") compB = "c" + b.displayText;
        else compB = "d" + b.displayText;

        const x = compA.toLowerCase();
        const y = compB.toLowerCase();
        if (x < y) return -1;
        if (x > y) return 1;
        return 0;
      });
      return sortedSymptoms;
    },
    evaluateCriteria(row) {
      return row.hasRulesets && row.lastRulesRunDate ? !!row.ruleIds.length : null;
    },
    facilityNameByUnitId(unitId) {
      let name = "";
      this.units.forEach(function (u) {
        if (u.id === unitId) {
          name = u.site.displayText;
        }
      });
      return name;
    },
    facilityNameByFacilityId(facId) {
      return this.sites.filter(s => s.id === facId).map(s => s.displayText)[0];
    },
    transformMedications(prescriptions) {
      const meds = this.medicationsLookup;
      return prescriptions.map(med => {
        const { frequency, dosage, medicationId } = med;
        const medication = meds[medicationId]?.displayText;
        return `${medication || ""} ${dosage || ""} ${frequency || ""}`;
      });
    },
    async exportObservationsToExcel() {
      this.detailsSection.exportingObservations = true;
      this.detailsSection.observationExportProgress = 0;
      let pageProgress = 0;
      let hasMorePages = true;
      const size = 1000;
      const showFacility = this.sites && this.sites.length > 1;
      const showCriteria = this.$configStore.rulesEngineEnabled();
      const columns = [
        "Name (MRN)",
        ...(showFacility ? ["Facility"] : []),
        "Onset Location",
        "Infection Site/Type",
        "Status",
        "Symptoms",
        ...(showCriteria ? ["Criteria"] : []),
        "Outcome",
        "Primary Organism",
        "Treatment",
        "Onset Date"
      ];

      const convertDataToColumns = record => {
        const name = this.getClientName(record.client);
        const id = this.isStaff(record.client)
          ? record.client.staffId
          : this.$configStore.patientIdType()
            ? record.client[this.$configStore.patientIdType()]
            : record.client.patientId;
        const facility = this.isStaff(record.client)
          ? (record.client.facilities || [])
            .map(this.facilityNameByFacilityId)
            .filter(s => s)
            .join(", ")
          : this.facilityNameByUnitId(record.unitId);
        const infectionSite = record.symptomType || record.infectionSite;
        const infection = `${infectionSite || ""}${record.infectionType ? ` / ${record.infectionType}` : ""}`;
        const symptoms = (this.sortedSymptom(record.symptoms) || []).map(s => s.displayText).join(", ");
        const criteria = this.evaluateCriteria(record);
        const criteriaText = criteria === true ? "Criteria Met" : criteria === false ? "Criteria Not Met" : "";
        const outcome = `${record.outcome || "Not Resolved"}${record.outcome && record.outcomeSubType ? ` / ${record.outcomeSubType}` : ""}`;
        const treatment = (record.medications || [])
          .map(med => {
            const { frequency, dosage, medication } = med;
            return `${medication || ""} ${dosage || ""} ${frequency || ""}`.trim();
          })
          .join(", ");
        const onsetDate = record.onsetDate ? moment(record.onsetDate).format(this.$configStore.dateFormat()) : "";

        return [
          `${name} (${id})`,
          ...(showFacility ? [facility] : []),
          record.location || "",
          infection,
          record.status ? "Confirmed" : "Suspected",
          symptoms,
          ...(showCriteria ? [criteriaText] : []),
          outcome,
          record.primaryOrganism || "",
          treatment,
          onsetDate
        ];
      };

      const getPageOfData = async page => {
        return await this.$http
          .post(`${window.CONFIG.infection_api}/search`, {
            ...this.global.filters,
            page,
            size
          })
          .then(response => response.body);
      };
      let data = [];
      while (hasMorePages) {
        this.detailsSection.exportingErrorDialog = false;
        if (!this.detailsSection.exportingObservations) {
          this.cancelObservationExport();
          break;
        }
        const json = await getPageOfData(pageProgress + 1).catch(err => {
          if (err.status === 0) {
            this.cancelObservationExport();
            this.detailsSection.exportingErrorDialog = true;
          }
        });
        this.detailsSection.observationExportProgress = Math.round(((pageProgress + 1) / json.totalPages) * 100);
        if (json.totalPages > pageProgress + 1) {
          pageProgress++;
        } else {
          hasMorePages = false;
        }
        data.push(json.content);
      }
      if (!this.detailsSection.exportingObservations) {
        return;
      }
      data = data.flat().map(convertDataToColumns);
      await this.createExcelFile(`uti-process-surveillance-${moment().format("YYYY-MM-DD")}.xlsx`, columns, data);
      this.detailsSection.exportingObservations = false;
      this.detailsSection.exportingErrorDialog = false;
    },
    createExcelFile: function (fileName, columns, data) {
      const detailsRow = util.generateExportDetailsRow();
      data.unshift(columns);
      data.unshift(detailsRow);
      return downloadXlsx(fileName, [{ sheet: "Sheet1", rows: data }]);
    },
    cancelObservationExport() {
      this.detailsSection.exportingObservations = false;
      this.detailsSection.observationExportProgress = 0;
    },
  },
  async mounted() {
    this.sites = this.$configStore.data.sites || [];
    this.units = this.$configStore.data.units || [];
    const config = await this.$http.get(`${window.CONFIG.treatement_api}/config`).then(resp => resp.json());
    this.medicationsLookup = util.arrayToObj(config.medications, "id");
  },
};
</script>

<style scoped>
.infection-case-detail-dialog>>>.el-dialog.el-dialog--large {
  width: 80% !important;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #333;
}

#app .card-content {
  font-size: 1rem;
}

#app .card-content strong {
  color: #000;
  font-size: 2rem;
}

.card-description {
  background: #f5f5f5;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin: 0.5rem 0 1rem;
  color: #555;
}

.el-tag {
  margin-right: 0.5em;
}

div.profile-row-photo {
  width: 4em;
  height: 4em;
  overflow: hidden;
  border-radius: 2em;
  border: solid 1px lightgray;
}

.profile-row-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: relative;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.color-code-label-0,
.color-code-label-1,
.color-code-label-2,
.color-code-label-3 {
  width: 1em;
  height: 1em;
  display: inline-block;
  vertical-align: middle;
  border: solid 1px lightgrey;
}

.color-code-label-0 {
  background: #00d560;
}

.color-code-label-1 {
  background: #0080ff;
}

.color-code-label-2 {
  background: #ffde25;
}

.color-code-label-3 {
  background: gray;
}

.symptom-status-image {
  width: 36px;
  position: relative;
  margin: auto;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.rx::after {
  content: ";";
}

.rx:last-child::after {
  content: "";
}

.clickable-rows>>>.el-table__body tbody tr {
  cursor: pointer;
}
</style>
